import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import ReactGA from 'react-ga4';
import { WifiOff, RefreshCw, CheckCircle2 } from "lucide-react";
import Landing from "./pages/Landing";
import Home from "./pages/Home";
import Contact from "./pages/Contact";
import Technology from "./pages/Technology";
import Upload from "./pages/Upload";
import Search from "./pages/Search";
import Map from "./pages/Map";
import Statistics from "./pages/Statistics";

import Navbar from "./components/common/Navbar";
import Footer from "./components/common/Footer";

import { GlobalProvider } from "./contexts/GlobalContext";

import InteractiveTable from "./components/stats/InteractiveTable"
import Chart from './components/stats/Chart'
import UsersTable from "./components/stats/UsersTable";
import LoginPage from "./pages/Login";
import ForgetPasswordPage from "./pages/ForgetPassword";
import SignupPage from "./pages/SignUp";
import Dashboard from "./pages/Dashboard";
import ClientPage from "./pages/Client";
import Stats from './components/dashboard/Stats';
import EditProfile from "./pages/EditProfile";
import ProfilePosts from "./components/features/ProfilePosts";
import VerifyPosts from "./components/dashboard/VerifyPosts";
import ViewAllUsers from "./components/dashboard/ViewUsers";
import UpdateGallery from './components/dashboard/UpdateGallery';
import ClientImpactDashboard from "./pages/ClientImpactDashboard.js";
import PlantProfile from "./pages/PlantProfile";
import ProjectsDashboard from "./components/dashboard/ProjectsDashboard";
import IndividualProjectDashboard from "./components/dashboard/IndividualProjectDashboard";
import ProjectDetailDashboard from "./components/dashboard/ProjectDetailDashboard";
import CreateProject from "./components/dashboard/CreateProject";
import ProjectGalleryIndividual from "./components/dashboard/ProjectGalleryIndividual";
import UserImpactDashboard from "./pages/UserImpactDashboard.js";
import Projects from './pages/Projects';
import ProjectPage from "./pages/ProjectPage.js";
import ProtectedRoute from "./components/ProtectedRoute.js";
import Leaders from "./pages/Leaders.js";
import VerifyHistory from "./components/dashboard/VerifyHistory.jsx";
import Activity from "./pages/Activity";
import ActivityPoints from "./pages/ActivityPoints.js";
import RedeemPoints from "./pages/RedeemPoints.js";
import ProfileVerification from "./pages/ProfileVerification.js";
import EmployeeTable from "./components/dashboard/EmployeeTable.jsx";
import ProjectContributors from "./components/dashboard/ProjectContributors.jsx";
import SelectArea from "./components/dashboard/SelectArea.jsx";
import PrivacyPolicy from "./pages/PrivacyPolicy.js";
import TermsAndConditions from "./pages/TermsAndConditions.js";
import SpeciesMaster from "./pages/SpeciesMaster.js";
import SpeciesForm from "./pages/SpeciesForm.jsx";
import Companies from "./pages/Companies.js";
import AdminMaster from "./pages/adminMaster.js";

import { keys } from "./config/keys.js";

const trackingId = keys.REACT_APP_GA_TRACKING_ID;
ReactGA.initialize(trackingId);

const mockData = {
  treeStages: {
    progress: [
      { label: "Seedling Stage", value: 20, color: "#87d068", },
      { label: "Planting and Tracking", value: 15, color: "#d30000", },
      { label: "Sapling Growth", value: 15, color: "#faad14" },
      { label: "Maturity", value: 50, color: "black" },
    ],
  },
  treeCO2e: {
    percentage: 75,
  },
};


function App() {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [showBackOnline, setShowBackOnline] = useState(false);
  const [isChecking, setIsChecking] = useState(false);

  useEffect(() => {
    ReactGA.send({ hitType: "pageview", page: window.location.pathname });
  }, []);

  useEffect(() => {
    const handleOffline = () => {
      setIsOffline(true);
    };

    const handleOnline = () => {
      setIsOffline(false);
      setShowBackOnline(true);
      const timer = setTimeout(() => {
        setShowBackOnline(false);
      }, 3500);
      return () => clearTimeout(timer);
    };

    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);

    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
    };
  }, []);

  const handleRetryConnection = () => {
    setIsChecking(true);
    setTimeout(() => {
      if (navigator.onLine) {
        setIsOffline(false);
        setShowBackOnline(true);
        setTimeout(() => setShowBackOnline(false), 3500);
      } else {
        setIsOffline(true);
      }
      setIsChecking(false);
    }, 1000);
  };

  return (
    <GlobalProvider> 
      <Router>
        {/* Offline Center Alert Modal */}
        {isOffline && (
          <div className="fixed inset-0 z-[999999] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 transition-all duration-300">
            <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 max-w-sm w-full p-6 text-center transform transition-all duration-300 scale-100 animate-in fade-in zoom-in-95">
              <div className="mx-auto w-14 h-14 bg-red-50 text-red-500 rounded-full flex items-center justify-center mb-4 ring-8 ring-red-50/50">
                <WifiOff className="w-7 h-7 stroke-[2.5]" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-1.5">You're Offline</h3>
              <p className="text-xs text-gray-500 mb-5 leading-relaxed">
                Please check your internet connection. Some features may be unavailable until connection is restored.
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleRetryConnection}
                  disabled={isChecking}
                  className="w-full flex items-center justify-center gap-2 py-2.5 px-4 bg-[#8BC34A] hover:bg-[#7cb342] active:scale-98 text-white text-xs font-bold rounded-xl transition-all shadow-md hover:shadow-lg disabled:opacity-75 cursor-pointer border-0"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${isChecking ? "animate-spin" : ""}`} />
                  <span>{isChecking ? "Checking Connection..." : "Retry Connection"}</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Back Online Notification Toast */}
        {showBackOnline && (
          <div className="fixed top-5 left-1/2 -translate-x-1/2 z-[999999] bg-emerald-600 text-white px-4 py-2.5 rounded-full shadow-lg flex items-center gap-2 text-xs font-semibold animate-in fade-in slide-in-from-top-2 duration-300">
            <CheckCircle2 className="w-4 h-4 text-emerald-200" />
            <span>Connection restored! You are back online.</span>
          </div>
        )}

        <Navbar/>
        <div>
          <Routes>
            <Route path="/" element={<Landing/>}/>
            <Route path="/verify" element={<ProfileVerification/>}/>
            <Route path="TreeRegistry" element={<Home/>}/>
            <Route path="Contact" element={<Contact/>}/>
            <Route path="Technology" element={<Technology/>}/>
            <Route path="Upload" element={<Upload/>}/>
            <Route path="trees" element={<Search/>}/>
            <Route path="map" element={<Map/>}/>
            <Route path="statistics" element={<Statistics/>}>
              <Route path="chart" element={<Chart/>}/>
              <Route path="geographic" element={<InteractiveTable/>}/>
              <Route path="top50users" element={<UsersTable/>}/>
            </Route>
            <Route path="login" element={<LoginPage/>} />
            <Route path="forget-password" element={<ForgetPasswordPage/>} />
            <Route path="signup" element={<SignupPage/>} />
            <Route path="profile" element={<EditProfile/>} />
            <Route path="projects" element={<Projects/>}/>
            <Route path="dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>}>
              <Route path="stats" element={<Stats/>}/>
              <Route path="profile" element={<EditProfile/>}/>
              <Route path="activity" element={<Activity/>}/>
              <Route path="mytrees" element={<ProfilePosts/>}/>
              <Route path="redeem" element={<RedeemPoints/>}/>
              
              {/* Admin Routes */}
              <Route path="verifytrees" element={<VerifyPosts/>}/>
              
              {/* <Route path="createuser" element={<CreateUser/>}/> */}
              <Route path="viewusers" element={<ViewAllUsers/>}/>
              <Route path="verifytreehistory" element={<VerifyHistory/>}/>
              <Route path="updategallery" element={<UpdateGallery/>}/>
              <Route path="rewardpoints" element={<ActivityPoints/>}/>
              
              {/* Admin Corporate Routes */}
              <Route path="projectdashboard/projectArea/:projectId" element={<SelectArea />} />
              <Route path="projectdashboard" element={<ProjectsDashboard/>}/>
              <Route path="projectdashboard/:id" element={<IndividualProjectDashboard/>}/>
              <Route path="projectdashboard/overview/:id" element={<ProjectDetailDashboard/>}/>
              <Route path="projectdashboard/newproject" element={<CreateProject/>}/>
              <Route path="myemployees" element={<EmployeeTable/>}/>
              <Route path="projectdashboard/updateprojectgallery/:id" element={<ProjectGalleryIndividual/>}/>
              <Route path="projectcontributors/:id" element={<ProjectContributors />} />
              <Route path="myspecies" element={<SpeciesMaster/>} />
              <Route path="myspecies/add" element={<SpeciesForm />} />
              <Route path="myspecies/edit/:id" element={<SpeciesForm />} />
              <Route path="companies" element={<Companies/>}/>
              <Route path="admin" element={<AdminMaster/>} />
            </Route>
            <Route path="clients" element={<ClientPage />} />
            <Route path="clientprofile/:clientId" element={<ClientImpactDashboard/>}/>
            <Route path="userprofile/:clientId" element={<UserImpactDashboard/>}/>
            <Route path="plantprofile/:treeId" element={<PlantProfile mockData={mockData}/>}/>
            <Route path="project/:projectid" element={<ProjectPage />} />
            <Route path="Leaders" element={<Leaders />} />
            <Route path="PrivacyPolicy" element={<PrivacyPolicy/>}/>
            <Route path="TermsAndConditions" element={<TermsAndConditions/>}/>
          </Routes>
        </div>
        <Footer/>
      </Router>
    </GlobalProvider>
  );
}

export default App;